import { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
      setFormData({ name: '', phone: '', email: '', location: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-transition" data-testid="contact-page">
      {/* Hero Section */}
      <section className="section-padding bg-richblack-light" data-testid="contact-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <div className="gold-line mb-6" />
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
              Contact <span className="text-gold-gradient">Us</span>
            </h1>
            <p className="text-lg text-white/70 leading-relaxed">
              Ready to discuss how EAGLENCE can help you? Fill out the form below 
              and our team will get back to you promptly.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding bg-richblack" data-testid="contact-form-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <div>
              {isSubmitted ? (
                <div className="bg-richblack-light border border-gold/20 p-12 text-center" data-testid="success-message">
                  <CheckCircle className="text-gold mx-auto mb-6" size={64} strokeWidth={1} />
                  <h3 className="font-serif text-2xl text-white mb-4">Thank You!</h3>
                  <p className="text-white/60 mb-8">
                    Your message has been received. We'll be in touch shortly.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="btn-secondary"
                    data-testid="send-another-btn"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label className="block text-white/80 text-sm mb-2 font-sans">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="input-style input-glow"
                      placeholder="Your full name"
                      data-testid="input-name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white/80 text-sm mb-2 font-sans">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="input-style input-glow"
                        placeholder="+1 (555) 000-0000"
                        data-testid="input-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm mb-2 font-sans">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="input-style input-glow"
                        placeholder="your@email.com"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white/80 text-sm mb-2 font-sans">
                      Location
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className="input-style input-glow"
                      placeholder="City, Country"
                      data-testid="input-location"
                    />
                  </div>

                  <div>
                    <label className="block text-white/80 text-sm mb-2 font-sans">
                      Your Message *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="input-style input-glow resize-none"
                      placeholder="Tell us about your project or inquiry..."
                      data-testid="input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full md:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-black border-t-transparent" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send size={18} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Info Panel */}
            <div className="lg:pl-8">
              <div className="bg-richblack-light border border-white/5 p-10" data-testid="info-panel">
                <h3 className="font-serif text-2xl text-white mb-6">
                  Let's Start a Conversation
                </h3>
                <p className="text-white/60 leading-relaxed mb-8">
                  Whether you're an artist looking for management, a label seeking 
                  distribution solutions, or an investor exploring catalog acquisition, 
                  we're here to help.
                </p>
                
                <div className="space-y-6">
                  <div className="border-l-2 border-gold pl-4">
                    <h4 className="text-white font-sans font-medium mb-1">Email Us</h4>
                    <a href="mailto:info@eaglence.tech" className="text-gold hover:text-gold-light text-sm transition-colors">
                      info@eaglence.tech
                    </a>
                  </div>
                  <div className="border-l-2 border-gold pl-4">
                    <h4 className="text-white font-sans font-medium mb-1">Quick Response</h4>
                    <p className="text-white/50 text-sm">We typically respond within 24-48 hours</p>
                  </div>
                  <div className="border-l-2 border-gold pl-4">
                    <h4 className="text-white font-sans font-medium mb-1">Confidential</h4>
                    <p className="text-white/50 text-sm">All inquiries are treated with strict confidentiality</p>
                  </div>
                  <div className="border-l-2 border-gold pl-4">
                    <h4 className="text-white font-sans font-medium mb-1">No Obligation</h4>
                    <p className="text-white/50 text-sm">Initial consultations are complimentary</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-8 bg-richblack border border-gold/20">
                <p className="text-gold font-serif text-lg italic">
                  "Eagle's Vision. Excellence Delivered."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
